import { isEven } from "@hellocarbo/lasagne/utils";
import { addYears } from "date-fns";

import { TTrackingCampaignDetails, TTrajectoryStep } from "@/types";

//// PROJECTION YEARS TO BE FILLED ////

// Generate all projection years that need to be filled (based on first and last projection years).
export function generateProjectionYearsToBeFilledIn(
  firstProjectionYear: number,
  lastProjectionYear: number
) {
  // Get all the years between firstProjectionYear and lastProjectionYear (endpoints included)
  const allYearsInInterval = [
    ...Array(lastProjectionYear - firstProjectionYear + 1).keys(),
  ].map((index) => firstProjectionYear + index);

  // If the difference between endpoints is > 4
  if (lastProjectionYear - firstProjectionYear > 4) {
    // If the endpoints have the same parity, return every 2 years
    if (isEven(firstProjectionYear) === isEven(lastProjectionYear)) {
      return allYearsInInterval.filter((_, index) => isEven(index));
    }

    // If they have different parities, return the firstProjectionYear
    // and every 2 years from firstProjectionYear + 1 (to be more precise at the beginning of the interval)
    return [
      firstProjectionYear,
      ...allYearsInInterval.slice(1).filter((_, index) => isEven(index)),
    ];
  }

  // If the difference is <= 4, return all the years of the interval
  return allYearsInInterval;
}

//// PROJECTION YEAR (2 CHOICES: EARLY CHOICE AND LATE CHOICE) ////

export const LATE_PROJECTION_YEAR = 2035;

// Calculate early projection year based on trajectory steps and related tracking campaign
export function getEarlyProjectionYear(
  trajectorySteps: Array<TTrajectoryStep>,
  trackingCampaign: TTrackingCampaignDetails
) {
  // If there are trajectory steps
  if (trajectorySteps.length) {
    const lastTrajectoryStepYear = trajectorySteps.slice(-1)[0].year;

    // If the year of the last step is equal to the LATE_PROJECTION_YEAR, return the year of the tracking campaign + 3
    if (lastTrajectoryStepYear === LATE_PROJECTION_YEAR) {
      return addYears(trackingCampaign.period_start, 3).getFullYear();
    }

    // If the year of the last step is not equal to the LATE_PROJECTION_YEAR, return this year
    return lastTrajectoryStepYear;
  }

  // If there are no trajectory steps, return the year of the tracking campaign + 3
  return addYears(trackingCampaign.period_start, 3).getFullYear();
}

export function getDefaultProjectionYear(
  earlyProjectionYear: number,
  lateProjectionYear: number,
  trajectorySteps: Array<TTrajectoryStep>
) {
  if (trajectorySteps.length) {
    const lastTrajectoryStepYear = trajectorySteps.slice(-1)[0].year;

    if (lastTrajectoryStepYear === lateProjectionYear) {
      return lateProjectionYear;
    }

    return earlyProjectionYear;
  }

  // By default (when there are no trajectory steps yet), the late projection year is selected
  return lateProjectionYear;
}

//// MISC ////

// Sort the trajectory steps by year.
export function sortTrajectoryStepsByYear(
  trajectorySteps: Array<TTrajectoryStep>
) {
  return trajectorySteps.sort((tsa, tsb) => tsa.year - tsb.year);
}
